import React, { useEffect } from 'react';
import * as THREE from 'three';
import TWEEN from '@tweenjs/tween.js';
import { isMobile } from 'react-device-detect';
import {
	getTextureKeys,
	loadCubemapTextures,
} from '@3jss/utils/textureHelpers';

const RESOLUTIONS = isMobile ? ['512', '1k', '2k', '4k'] : ['1k', '2k', '4k'];

const ENV_MAP_RESOLUTION = isMobile ? '512' : '1k';

const _3DScene = ({
	scene,
	renderer,
	backgroundUrl,
	envMapIntensity = 1.0,
	transition,
	imageIntegrity,
	useWebp,
	onBackgroundReady = () => {},
	onBackgroundLoaded = () => {},
	onBackgroundError = () => {},
	readyAt = isMobile ? '512' : '1k',
	maxLevel = isMobile ? '512' : '1k',
	useSceneBackgroundAsEnvMap = false,
	sceneType,
}) => {
	useEffect(() => {
		if (backgroundUrl) {
			triggerTransition();

			loadProgressive().then(() => {
				sceneType !== '3d_model_scene' &&  onBackgroundLoaded();
			});
		}
	}, [backgroundUrl]);

	useEffect(() => {
		return () => {
			scene.background = null;
			scene.environment = null;
		};
	}, []);

	const setEnvironmentMap = (texture) => {
		scene.environment = texture;
		scene.environmentRotation = new THREE.Euler(0, Math.PI, 0, 'XYZ');
		scene.environmentIntensity = envMapIntensity;
	};

	const loadProgressive = async () => {
		for (let level of RESOLUTIONS) {
			try {
				await loadBackgroundAtLevel(level);
			} catch (e) {
				onBackgroundError(e);
			}
			if (level === readyAt) {
				sceneType !== '3d_model_scene' && onBackgroundReady();
			}
			if (level === maxLevel) {
				break;
			}
		}
	};

	const loadBackgroundAtLevel = (level) => {
		return new Promise((resolve, reject) => {
			const keys = getTextureKeys(level, imageIntegrity, useWebp);

			loadCubemapTextures(backgroundUrl, keys, 'bitmap')
				.then((texture) => {
					texture.colorSpace = THREE.SRGBColorSpace;

					scene.background = texture;
					// Rotate background to correct orientation
					scene.backgroundRotation = new THREE.Euler(
						0,
						Math.PI,
						0,
						'XYZ',
					);
					if (useSceneBackgroundAsEnvMap && level === ENV_MAP_RESOLUTION) {
						setEnvironmentMap(texture);
					}
					resolve();
				})
				.catch((e) => {
					reject(e);
				});
		});
	};

	const createCanvasContext = (canvas) => {
		canvas.width = window.innerWidth;
		canvas.height = window.innerHeight;
		return canvas.getContext('2d');
	};

	const zoomNFade = () => {
		const copyCanvas = document.createElement('canvas');
		const copyCanvasContext = createCanvasContext(copyCanvas);

		const effectCanvas = document.createElement('canvas');
		const root = document.getElementById('canvas-wrapper');
		effectCanvas.style.zIndex = 10;
		effectCanvas.style.position = 'absolute';
		effectCanvas.style.top = 0;
		effectCanvas.style.left = 0;
		effectCanvas.style.pointerEvents = 'none';
		root.appendChild(effectCanvas);
		const effectCanvasContext = createCanvasContext(effectCanvas);

		const domEl = renderer.domElement;
		copyCanvasContext.drawImage(domEl, 0, 0);
		effectCanvasContext.drawImage(copyCanvas, 0, 0);

		const width = window.innerWidth;
		const height = window.innerHeight;

		const options = { opacity: 1, scale: 1 };

		new TWEEN.Tween(options)
			.to({ opacity: 0, scale: 2 }, 1200)
			.easing(TWEEN.Easing.Quadratic.Out)
			.onUpdate(() => {
				effectCanvasContext.clearRect(0, 0, width, height);
				effectCanvasContext.globalAlpha = options.opacity;

				const x = width / 2 - (width / 2) * options.scale;
				const y = height / 2 - (height / 2) * options.scale;

				effectCanvasContext.drawImage(
					copyCanvas,
					x,
					y,
					width * options.scale,
					height * options.scale,
				);
			})
			.start()
			.onComplete(() => {
				effectCanvasContext.clearRect(0, 0, width, height);
				root.removeChild(effectCanvas);
				scene.removeAnimationHook('backgroundAnimation');
			});

		scene.addAnimationHook('backgroundAnimation', TWEEN.update);
	};

	const triggerTransition = () => {
		if (transition && transition.enable) {
			if (transition.type === 'zoom & fade') {
				zoomNFade();
			}
		}
	};

	return null;
};

export default _3DScene;
